<script setup lang="ts">
import type { CmsElementImageGallery } from "@shopware/composables";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useCmsElementConfig } from "#imports";
import { isSpatial } from "@shopware/cms-base-layer/helpers/media/isSpatial";

const props = withDefaults(
  defineProps<{
    content: CmsElementImageGallery;
    slidesToShow?: number;
    slidesToScroll?: number;
  }>(),
  {
    slidesToShow: 5,
    slidesToScroll: 4,
  }
);

const { getConfigValue } = useCmsElementConfig(props.content);

const speed = ref<number>(300);
const currentIndex = ref(0);
const currentThumb = ref(0);
// const imageSlider = useTemplateRef("imageSlider");
const imageThumbsTrack = useTemplateRef("imageThumbsTrack");
const isLoading = ref(true);
const imageThumbsTrackStyle = ref({});
// const imageThumbs = useTemplateRef("imageThumbs");
const imageThumbsStyle = ref({});
const mediaGallery = computed(() => props.content.data?.sliderItems ?? []);
const galleryPosition = computed<string>(
  () => getConfigValue("galleryPosition") ?? "left"
);
const scrollPx = ref(0);

onMounted(() => {
  // initThumbs();
});

const { product } = useProduct();

/*
function initThumbs() {
  if (imageThumbsTrack.value) {
    setTimeout(() => {
      if (galleryPosition.value === "left") {
        const clientHeight = imageThumbsTrack.value?.clientHeight ?? 0;
        scrollPx.value = clientHeight / mediaGallery.value.length;
        imageThumbsStyle.value = {
          height: `${scrollPx.value * +props.slidesToShow}px`,
        };
      } else {
        const clientWidth = imageThumbs.value?.clientWidth ?? 0;
        scrollPx.value = clientWidth / props.slidesToShow;
        imageThumbsTrackStyle.value = {
          width: `${scrollPx.value * mediaGallery.value.length}px`,
        };
      }
      isLoading.value = false;
    }, 100);
  }
}

function changeCover(i: number) {
  if (i === currentIndex.value) return;
  imageSlider.value?.goToSlide(i);
}

function handleChangeSlide(e: number) {
  currentIndex.value = e;
  if (currentIndex.value > currentThumb.value + props.slidesToShow - 1) {
    move("next", currentIndex.value);
    return;
  }
  if (currentIndex.value < currentThumb.value) {
    move("previous", currentIndex.value);
    return;
  }
}

function move(type: "next" | "previous", specificIndex?: number | string) {
  let step: number;
  const index =
    typeof specificIndex !== "number"
      ? Number.parseInt(specificIndex as string)
      : specificIndex;
  if (index >= 0) {
    if (type === "next") {
      step =
        index + props.slidesToScroll < mediaGallery.value.length
          ? index
          : mediaGallery.value.length - props.slidesToShow;
    } else {
      step =
        index - props.slidesToScroll > 0 ? index - props.slidesToScroll : 0;
    }
  } else {
    if (type === "next") {
      step =
        currentThumb.value + props.slidesToShow - 1 + props.slidesToScroll <
        mediaGallery.value.length
          ? currentThumb.value + props.slidesToScroll
          : mediaGallery.value.length - props.slidesToShow;
    } else {
      step =
        currentThumb.value - props.slidesToScroll > 0
          ? currentThumb.value - props.slidesToScroll
          : 0;
    }
  }
  currentThumb.value = step;
  let xAxis = 0;
  let yAxis = 0;
  if (galleryPosition.value === "left") {
    yAxis = scrollPx.value * currentThumb.value;
  } else {
    xAxis = scrollPx.value * currentThumb.value;
  }
  imageThumbsTrackStyle.value = {
    ...imageThumbsTrackStyle.value,
    transform: `translate3d(-${xAxis}px, -${yAxis}px, 0px)`,
    transition: `transform ${speed.value}ms ease 0s`,
  };
}

function previous() {
  if (currentThumb.value <= 0) {
    return;
  }
  move("previous");
}

function next() {
  if (currentThumb.value + props.slidesToShow >= mediaGallery.value.length) {
    return;
  }
  move("next");
}

*/

const imageThumbs = ref(null);
const imageSlider = ref(null);

const imageThumbsSwiper = useSwiper(imageThumbs, {
  effect: "slide",
  loop: true,
  grabCursor: true,
  slidesPerView: 1,
});

const imageSliderSwiper = useSwiper(imageSlider, {
  effect: "slide",
  loop: true,
  grabCursor: true,
  slidesPerView: 1,
});

// Add this function to handle thumbnail clicks
function handleThumbClick(index: number) {
  if (imageSliderSwiper) {
    imageSliderSwiper.to(index);
  }
}
</script>

<template>
  <div
    :class="{
      'flex gap-5': true,
      'flex-col-reverse gap-10': galleryPosition === 'underneath',
    }"
  >
    <div
      class="hidden md:block"
      :class="{
        'hidden lg:flex w-[100px] relative flex-col items-center':
          galleryPosition === 'left',
        'flex relative w-full': galleryPosition === 'underneath',
      }"
    >
      <div class="w-full">
        <div
          ref="imageThumbsTrack"
          class="gap-y-4 flex flex-wrap"
          :class="{
            flex: true,
            'flex-col': galleryPosition === 'left',
          }"
        >
          <div
            v-for="(image, i) in mediaGallery"
            :key="image.media.url"
            :class="{
              'md:w-1/3 px-1 md:px-2': galleryPosition === 'underneath',
              'w-full': galleryPosition === 'underneath',
            }"
            @click="handleThumbClick(i)"
            class="cursor-pointer"
          >
            <figure class="img-cover aspect-square rounded-3xl overflow-hidden">
              <div v-if="isSpatial(image.media)" class="h-full relative">
                <CmsElementImageGallery3dPlaceholder
                  class="w-full h-full object-center"
                />
                <span
                  class="absolute bottom-0 text-sm bg-gray rounded px-1 text-white"
                >
                  3D</span
                >
              </div>
              <img
                v-else
                loading="lazy"
                :src="image.media.url"
                alt="Product image"
                class="w-full h-full object-center"
              />
            </figure>
          </div>
        </div>
      </div>
    </div>
    <div class="w-full overflow-hidden">
      <div
        v-if="mediaGallery.length"
        @click="imageSliderSwiper.prev()"
        class="swiper-cust-button-prev bg-cust_cream text-cust_gray03 md:hidden"
      >
        <i class="fa-solid fa-arrow-left"></i>
      </div>
      <swiper-container ref="imageSlider">
        <swiper-slide
          v-for="(image, index) of mediaGallery"
          :key="image.media.url"
        >
          <div class="relative mb-4 mx-2">
            <figure class="img-cover aspect-square rounded-3xl overflow-hidden">
              <NuxtImg
                :src="image?.media?.url"
                :alt="image?.media?.alt"
                :width="image?.media?.metaData?.width"
                :height="image?.media?.metaData?.height"
              />
            </figure>
            <div
              v-if="product.markAsTopseller && index === 0"
              class="img-tags w-[120px]"
            >
              <span class="tag_pink w-auto font-redit_sans_con text-22px">
                {{ $t("product.badges.topseller") }}
              </span>
            </div>
          </div>
        </swiper-slide>
      </swiper-container>
      <div
        v-if="mediaGallery.length"
        @click="imageSliderSwiper.next()"
        class="swiper-cust-button-next bg-cust_cream text-cust_gray03  md:hidden"
      >
        <i class="fa-solid fa-arrow-right"></i>
      </div>
    </div>
  </div>
</template>
